import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { ChangePasswordSchema } from '@datazo/contracts';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiClientError } from '@/shared/api/client';
import { useAuthStore } from '@/shared/store/auth';
import { usePageTitle } from '@/shared/store/page';
import { useToast } from '@/shared/toast/toast-context';
import { cn } from '@/shared/lib/cn';
import { useChangePassword } from './api';

const MIN_LEN = 12;

interface FieldErrors {
  actual?: string;
  nueva?: string;
  confirmacion?: string;
}

export function ConfiguracionPage(): JSX.Element {
  usePageTitle('Configuración', 'Sistema · Mi cuenta');
  const user = useAuthStore((s) => s.user);
  const clearMustChangePassword = useAuthStore((s) => s.clearMustChangePassword);
  const { showToast } = useToast();
  const changePassword = useChangePassword();

  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const checks = useMemo(
    () => ({
      longitud: nueva.length >= MIN_LEN,
      mayuscula: /[A-ZÁÉÍÓÚÑ]/.test(nueva),
      minuscula: /[a-záéíóúñ]/.test(nueva),
      numero: /\d/.test(nueva),
    }),
    [nueva],
  );
  const fortaleza = Object.values(checks).filter(Boolean).length;

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setErrors({});

    const parsed = ChangePasswordSchema.safeParse({ actual, nueva, confirmacion });
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      setErrors({
        actual: flat.actual?.[0],
        nueva: flat.nueva?.[0],
        confirmacion: flat.confirmacion?.[0],
      });
      return;
    }

    try {
      await changePassword.mutateAsync(parsed.data);
      // El backend ya limpió mustChangePassword; reflejarlo en la sesión para
      // liberar el redirect forzado de RequireAuth.
      clearMustChangePassword();
      showToast({
        kind: 'success',
        title: 'Contraseña actualizada',
        description: 'Tu contraseña se cambió correctamente.',
      });
      setActual('');
      setNueva('');
      setConfirmacion('');
    } catch (err) {
      const message =
        err instanceof ApiClientError ? err.message : 'No fue posible cambiar la contraseña.';
      // El backend marca la contraseña actual incorrecta como ValidationError (400).
      if (err instanceof ApiClientError && /actual/i.test(message)) {
        setErrors({ actual: message });
      }
      showToast({
        kind: 'danger',
        title: 'No se pudo cambiar la contraseña',
        description: message,
      });
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <Card>
        <form onSubmit={handleSubmit} noValidate>
          <CardHeader>
            <div className="flex items-center gap-3">
              <span className="inline-grid place-items-center w-10 h-10 rounded-md bg-primary/10 text-primary">
                <KeyRound className="w-5 h-5" />
              </span>
              <div>
                <CardTitle>Cambiar mi contraseña</CardTitle>
                <CardDescription>
                  {user
                    ? `Sesión: ${user.nombre} · ${user.correo}`
                    : 'Actualiza la contraseña de tu cuenta'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-5">
            <Field
              id="actual"
              label="Contraseña actual"
              value={actual}
              onChange={setActual}
              show={show}
              error={errors.actual}
              autoComplete="current-password"
            />

            <div className="space-y-2">
              <Field
                id="nueva"
                label="Nueva contraseña"
                value={nueva}
                onChange={setNueva}
                show={show}
                error={errors.nueva}
                autoComplete="new-password"
              />
              {nueva.length > 0 && (
                <div className="space-y-2 pt-1">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <span
                        key={i}
                        className={cn(
                          'h-1.5 flex-1 rounded-full transition-colors',
                          i < fortaleza
                            ? fortaleza <= 2
                              ? 'bg-danger'
                              : fortaleza === 3
                                ? 'bg-warning'
                                : 'bg-success'
                            : 'bg-border',
                        )}
                      />
                    ))}
                  </div>
                  <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-text-muted">
                    <Requisito ok={checks.longitud}>Mínimo {MIN_LEN} caracteres</Requisito>
                    <Requisito ok={checks.mayuscula}>Una mayúscula</Requisito>
                    <Requisito ok={checks.minuscula}>Una minúscula</Requisito>
                    <Requisito ok={checks.numero}>Un número</Requisito>
                  </ul>
                </div>
              )}
            </div>

            <Field
              id="confirmacion"
              label="Confirmar nueva contraseña"
              value={confirmacion}
              onChange={setConfirmacion}
              show={show}
              error={errors.confirmacion}
              autoComplete="new-password"
            />

            <label className="flex items-center gap-2 text-sm text-text-soft cursor-pointer select-none">
              <input
                type="checkbox"
                checked={show}
                onChange={(e) => setShow(e.target.checked)}
                className="rounded border-border text-primary focus:ring-primary/20"
              />
              Mostrar contraseñas
            </label>
          </CardContent>

          <CardFooter className="flex items-center justify-between gap-3">
            <p className="flex items-center gap-1.5 text-xs text-text-muted">
              <ShieldCheck className="w-4 h-4 text-success" />
              Tu sesión seguirá activa tras el cambio.
            </p>
            <Button type="submit" disabled={changePassword.isPending}>
              {changePassword.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Cambiar contraseña
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  error?: string | undefined;
  autoComplete: string;
}

function Field({ id, label, value, onChange, show, error, autoComplete }: FieldProps): JSX.Element {
  const [reveal, setReveal] = useState(false);
  const visible = show || reveal;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className={cn('pr-10', error && 'border-danger focus-visible:border-danger')}
          aria-invalid={error ? true : undefined}
        />
        <button
          type="button"
          onClick={() => setReveal((r) => !r)}
          className="absolute inset-y-0 right-0 grid place-items-center w-10 text-text-muted hover:text-text"
          tabIndex={-1}
          aria-label={visible ? 'Ocultar' : 'Mostrar'}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

function Requisito({ ok, children }: { ok: boolean; children: ReactNode }): JSX.Element {
  return (
    <li className={cn('flex items-center gap-1.5', ok ? 'text-success' : 'text-text-muted')}>
      <span
        className={cn(
          'inline-block w-1.5 h-1.5 rounded-full',
          ok ? 'bg-success' : 'bg-border-strong',
        )}
      />
      {children}
    </li>
  );
}
